import {Badge, Divider, Image, List, Select, Table, Typography} from '@douyinfe/semi-ui';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneLight} from 'react-syntax-highlighter/dist/cjs/styles/prism';
import './MarkdownRender.css'
import {useContext, useEffect, useMemo, useState} from 'react';
import {MenuContext} from '../App';

class HierarchyGenerator {
    constructor() {
        this.sources = [];
    }
    
    pushData(item) {
        this.sources.push({...item, children: []})
    }
    
    getHierarchy() {
        const hierarchy = [];
        const stack = [];
    
        this.sources.forEach(item => {
            
            while (stack.length > 0 && stack[stack.length - 1].level >= item.level) {
                stack.pop();
            }
            if (stack.length > 0) {
                const parent = stack[stack.length - 1];
                if (!parent.children) {
                    parent.children = [];
                }
                item = {...item, key: `${parent?.key}.${(parent?.children?.length || 0) + 1}`}
                parent.children.push(item);
            } else {
                item = {...item, key: `${hierarchy.length + 1}`}
                hierarchy.push(item);
            }
            stack.push(item);
        });
    
        return hierarchy;
    }
}

export function MarkdownRender ({markdown}) {
    
    const {menus, setMenus} = useContext(MenuContext)
    let list = []
    const hg = new HierarchyGenerator()
    
    useEffect(() => {
        try {
            // PC端目录渲染，移动端组件没有包裹context，过滤一下
            const data = hg.getHierarchy()
            setMenus(data)
        }catch (e) {
            console.log('error', e)
        }
    }, [])
    
    
    
    const extractValues = nodes => {
        const tableArray = [];
        
        function traverse(node) {
            if (node.type === "element") {
                if (node.tagName === "tr") {
                    let rowArray = {};
                    if (node.children) {
                        for (let index = 0; index < node.children.length; index++) {
                            const child = node.children.at(index)
                            if (child.type === "element" && (child.tagName === "th" || child.tagName === "td")) {
                                const value = child.children[0].value || '';
                                rowArray[index] = value.trim()
                            }
                        }
                    }
                    if (Object.values(rowArray).length > 0) {
                        tableArray.push(rowArray);
                    }
                } else {
                    if (node.children) {
                        for (const child of node.children) {
                            traverse(child);
                        }
                    }
                }
            }
        }
        
        traverse(nodes);
        return tableArray;
    }
    const tagMaker = (children, tagName, index=0) => {
        children = children.join('')
        let tagLevel = Number(tagName?.replace('h', ''))
        let id = `${children}-${tagLevel}-${list.length+1}`.replace(/[^\u4e00-\u9fa5a-zA-Z0-9-]/g, '').replace(/^[0-9]*/g, '')
        list.push({text: children, level: tagLevel, id, children: [] })
        hg.pushData({text: children,  level: tagLevel, id})
        return id
    }
    
    const {Title, Text, Paragraph} = Typography
    const {Column} = Table
    
    return (
        <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            children={markdown}
            components={{
                pre ({children}) {
                    return <pre style={{width: '100%'}} > {children} </pre>
                },
                img ({ ...data }) {
                    return (
                        <Image {...data} style={{ border: '1px solid rgba(var(--semi-grey-1), 1)', borderRadius: 5 }} width={'100%'} />
                    )
                },
                code({ inline, className, children, ...data }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                        <div>
                            <SyntaxHighlighter className={"code-highlighting"} style={ oneLight } language={match[1]} PreTag="div" >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        </div>
                    ) : <span className={'line-code'} > {children} </span>
                },
                blockquote: ({ children }) => {
                    const nodes = children.filter(item => typeof item === 'object')
                    return (
                        <blockquote
                            style={{
                                background: 'rgba(var(--semi-blue-0), 1)',
                                paddingTop: 10,
                                paddingBottom: 10,
                                paddingLeft: 10,
                                paddingRight: 10,
                                borderRadius: 3,
                                width: '95%',
                                borderLeft: '5px solid rgba(var(--semi-blue-3), 1)',
                                margin: 0,
                            }}
                        >
                            {nodes.map(item => {
                                const value = item?.props?.children ?? item
                                return  <Paragraph type="tertiary" style={{color: 'rgba(var(--semi-grey-5), 1)'}}> {value} </Paragraph>
                            })}
                        </blockquote>
                    
                    );
                },
                hr () { return <Divider margin='15px' style={{borderWidth: 'thick'}} /> },
                p ({ children }) {
                    return <Paragraph spacing={'extended'} style={{width: '100%'}}>{children}</Paragraph>
                },
                a ({href, children}) {
                    return <Text link={{href: href, target: '_blank'}} >{children}</Text>
                },
                link ({ href, children }) {
                    return <Text link={{href: href}} >{children}</Text>
                },
                li ({ children }) {
                    const nodes = children.filter(item => typeof item === 'object')
                    return (
                        <List.Item style={{width: '100%'}} >
                            <List
                                style={{ border: '1px solid var(--semi-color-border)', flexBasis: '100%', flexShrink: 0, width: '100%' }}
                                split={false}
                                bordered={false}
                                dataSource={nodes}
                                renderItem={item => {
                                    return <List.Item style={{width: '100%', paddingRight: 10}} className={'row-list-item'} > {item} </List.Item>
                                }}
                            />
                        </List.Item>
                    )
                },
                ul ({ children }) {
                    return <List bordered={false} split={false} style={{width: '100%'}} > {children} </List>
                },
                ol ({ children }) {
                    let childList = children.filter(item => item?.props?.children)
                    return (
                        // <List bordered={false} split={false} style={{width: '100%'}} >
                        //     { childList = childList.map((item, index) => <List.Item style={{flex: 0, width: '100%'}} header={<Badge type={'secondary'} count={index+1} />} main={<div style={{color: 'var(--semi-color-text-0)', fontWeight: 500, width: '100%'}}>{item?.props?.children}</div>} />) }
                        // </List>
                        <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                            { childList = childList.map((item, index) => (
                                <div style={{display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'baseline', paddingBottom: 10}}>
                                    <Badge type={'secondary'} count={index+1} />
                                    <div style={{display: 'flex', flexDirection: 'column', width: '95%', paddingLeft: 10}}> <div>{item?.props?.children}</div> </div>
                                </div>
                            )) }
                        </div>
                    )
                },
                h1 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={1} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                h2 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={2} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                h3 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={3} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                h4 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={4} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                h5 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={5} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                h6 ({ children, node }) {
                    let id = tagMaker(children, node?.tagName)
                    return <Title heading={6} style={{ margin: '8px 0' }} id={id} > {children} </Title>
                },
                strong ({ children, ...data }) {
                    return <Text strong> { children } </Text>
                },
                text ({ children }) {
                    return <Text>{children}</Text>
                },
                table ({children, node}) {
                    const tbody = node.children.filter(item => item?.tagName === 'tbody')?.at(0)
                    const thead = node.children.filter(item => item?.tagName === 'thead')?.at(0)
                    const headValue = extractValues(thead)
                    const bodyValue = extractValues(tbody)
                    return (
                        <Table
                            dataSource={bodyValue}
                            pagination={false}
                            size={'small'}
                            onRow={(record, index) => index % 2 === 0 ? ({style: {background: 'var(--semi-color-fill-0)'}}) : ({})}
                        >
                            {
                                headValue.map(item => Object.keys(item).map(key => <Column title={item[key]} dataIndex={key} />))
                            }
                        </Table>
                    )
                },
                select ({children, node}) {
                    let nodes = node.children.filter(item => item.type === 'element')
                    nodes = nodes.map(item => {
                        if (item.tagName === 'optgroup') {
                            return { type: 'optgroup', label: item.properties.label, children: item.children.filter(child => child.type === 'element').map(item => ({type: 'option', value: item.properties.value, text: item.children[0].value}))}
                        }
                        return {type: 'option', value: item.properties.value, text: item.children[0].value}
                    })
                    return <Select style={{width: 120}}> {nodes.map(item => item.type === 'optgroup' ? <Select.OptGroup label={item.label}>{item.children.map(child => <Select.Option value={child.value}>{child.text}</Select.Option>)}</Select.OptGroup> : <Select.Option value={item.value} >{item.text}</Select.Option>)} </Select>
                },
                span ( {children, style} ) {
                    return <Text style={style} > {children} </Text>
                },
            }}
        />
    )
}